import React, { useState, ChangeEvent } from "react";
import {
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  Container,
  IconButton,
  Stack,
  Grid,
  InputLabel,
  Theme,
  SxProps,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { ScrapeTimeDto } from "@swagger/typescript-fetch-goflyer";
import MultipleSelect from "../SelectMui";

interface DayTime {
  day: ScrapeTimeDto.DayEnum;
  time: string;
  id: string;
}

interface DayTimeListManagerProps<T extends DayTime> {
  dayTimeValues?: T[];
  onUpdateDayTime?: (dayTimeValues: T[]) => void;
  sx?: SxProps<Theme>;
}

const DayTimeListManager = <T extends DayTime>({
  dayTimeValues = [],
  onUpdateDayTime,
  sx = { textAlign: "center" },
}: DayTimeListManagerProps<T>) => {
  const { t } = useTranslation();

  const [day, setDay] = useState<ScrapeTimeDto.DayEnum | "">("");
  const [time, setTime] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const dayEnumObject = Object.keys(ScrapeTimeDto.DayEnum).reduce(
    (acc, key) => {
      const value = ScrapeTimeDto.DayEnum[
        key as keyof typeof ScrapeTimeDto.DayEnum
      ]
        ?.toString()
        ?.toLowerCase();
      acc[value] = key;
      return acc;
    },
    {} as { [key: string]: string }
  );

  const weekDaysList = Object.values(dayEnumObject)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      value:
        ScrapeTimeDto.DayEnum[
          key.toLowerCase() as keyof typeof ScrapeTimeDto.DayEnum
        ],
      label: key,
    }));

  const handleAddOrUpdateKeyValue = () => {
    if (onUpdateDayTime) {
      let updatedKeyValues;
      if (isEditing && editingId !== null) {
        updatedKeyValues = dayTimeValues.map((kv) =>
          kv.id === editingId ? { ...kv, day, time } : kv
        );
      } else {
        const newKeyValue = {
          day,
          time,
          id: new Date().toISOString(),
        } as T;
        updatedKeyValues = [...dayTimeValues, newKeyValue];
      }
      onUpdateDayTime(updatedKeyValues);
    }
    resetFields();
  };

  const resetFields = () => {
    setDay("");
    setTime("");
    setIsEditing(false);
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    if (onUpdateDayTime) {
      onUpdateDayTime(dayTimeValues.filter((kv) => kv.id !== id));
    }
  };

  const handleEdit = (id: string) => {
    const kv = dayTimeValues.find((kv) => kv.id === id);
    if (kv) {
      const day = kv.day.toString()?.toLowerCase();
      setDay(ScrapeTimeDto.DayEnum[day as keyof typeof ScrapeTimeDto.DayEnum]);
      setTime(kv.time);
      setIsEditing(true);
      setEditingId(kv.id);
    }
  };

  return (
    <Container component="main" sx={sx}>
      <Stack
        direction={"row"}
        alignItems={"end"}
        columnGap={2}
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        rowGap={"15px"}
      >
        <Stack direction={"column"} alignItems={"start"} rowGap={"4px"}>
          <InputLabel> {t(messages.Day())}</InputLabel>
          <MultipleSelect
            selectOptions={weekDaysList}
            selectedValue={day}
            onSelect={(value) => {
              setDay(value as ScrapeTimeDto.DayEnum);
            }}
          />
        </Stack>
        <Stack direction={"column"} alignItems={"start"} rowGap={"4px"}>
          <InputLabel> {t(messages.TimeFormatHHmm())}</InputLabel>

          <TextField
            variant="outlined"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setTime(e.target.value);
            }}
            value={time}
            inputProps={{ maxLength: 4 }}
            sx={{
              "& label.Mui-focused": {
                color: "#ff8038",
              },
              "& .MuiFormLabel-root ": {
                top: "-7px",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ff8038",
                },
                "&:hover fieldset": {
                  borderColor: "#ff8038",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ff8038",
                },
                "& input": {
                  padding: "7.5px",
                },
              },
            }}
          />
        </Stack>
        <Button
          variant={"contained"}
          onClick={handleAddOrUpdateKeyValue}
          disabled={!day || !time}
          sx={{
            textTransform: "capitalize",
            height: "38px",
            bgcolor: "#ff8038",
            "&:hover": {
              bgcolor: "#ff8038",
            },
          }}
        >
          {isEditing ? t(messages.Update()) : t(messages.Add())}
        </Button>
      </Stack>
      <List
        sx={{ mt: 2, maxHeight: "400px", overflowY: "auto", borderRadius: 2 }}
      >
        <ListItem
          divider
          sx={{
            display: "flex",
            justifyContent: "space-between",
            bgcolor: "background.paper",
            pt: "2px",
            pb: "2px",
            boxShadow: 0,
            borderBottom: 0,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography sx={{ flexGrow: 1, textTransform: "capitalize" }}>
                {t(messages.Day())}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ flexGrow: 1, textTransform: "capitalize" }}>
                {t(messages.Time())}
              </Typography>
            </Grid>
          </Grid>
          <Typography
            sx={{
              flexGrow: 1,
              textTransform: "capitalize",
              width: "90px",
              textAlign: "center",
            }}
          >
            {t(messages.Actions())}
          </Typography>
        </ListItem>
        {dayTimeValues.map((kv, index) => (
          <ListItem
            key={kv.id}
            divider
            sx={{
              display: "flex",
              justifyContent: "space-between",
              bgcolor: index % 2 === 0 ? "#e0e0e0b5" : "background.paper",
              pt: "2px",
              pb: "2px",
              boxShadow: 0,
              borderBottom: 0,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography sx={{ flexGrow: 1 }}>{kv.day}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ flexGrow: 1 }}>{kv.time}</Typography>
              </Grid>
            </Grid>

            <Stack direction={"row"}>
              <IconButton
                onClick={() => handleEdit(kv.id)}
                color="primary"
                sx={{ color: "#ff8038" }}
              >
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDelete(kv.id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Stack>
          </ListItem>
        ))}
      </List>
    </Container>
  );
};

export default DayTimeListManager;
